import { observable, makeObservable, action } from 'mobx';
import React from 'react';

class AIPPTStore {
    constructor() {
        makeObservable(this);
    }

    // AIPPT 独有的状态
    @observable aipptLoading = false; // AIPPT 加载状态
    @observable messages = []; // 本地消息状态
    @observable loadingMessageSessionId = null; // 跟踪加载消息所在的sessionId
    @observable selectedTemplate = null; // 选中的模板
    @observable selectedColor = 0; // 选中的主题色，默认蓝色
    @observable selectedStyle = '科技风'; // 选中的风格
    @observable loadingCovers = {}; // 封面加载状态对象，key为recordId
    @observable coverImages = []; // 封面图片列表
    @observable apiRequestStatus = {
        isPending: false, // 是否有请求正在进行
        requestId: null, // 当前请求的唯一ID
        requestType: null, // 请求类型：'cover', 'template', 'generate'
        startTime: null // 请求开始时间
    };

    // AIPPT 独有的状态设置方法
    @action setAipptLoading = (aipptLoading) => {
        this.aipptLoading = aipptLoading;
    };

    @action setMessages = (messages) => {
        if (typeof messages === 'function') {
            this.messages = messages(this.messages);
        } else {
            this.messages = messages;
        }
    };

    @action setLoadingMessageSessionId = (loadingMessageSessionId) => {
        this.loadingMessageSessionId = loadingMessageSessionId;
    };

    @action setSelectedTemplate = (selectedTemplate) => {
        this.selectedTemplate = selectedTemplate;
    };

    @action setSelectedColor = (selectedColor) => {
        this.selectedColor = selectedColor;
    };

    @action setSelectedStyle = (selectedStyle) => {
        this.selectedStyle = selectedStyle;
    };

    @action setLoadingCovers = (loadingCovers) => {
        if (typeof loadingCovers === 'function') {
            this.loadingCovers = loadingCovers(this.loadingCovers);
        } else {
            this.loadingCovers = loadingCovers;
        }
    };

    @action setCoverImages = (coverImages) => {
        if (typeof coverImages === 'function') {
            this.coverImages = coverImages(this.coverImages);
        } else {
            this.coverImages = coverImages;
        }
    };

    @action setApiRequestStatus = (apiRequestStatus) => {
        if (typeof apiRequestStatus === 'function') {
            this.apiRequestStatus = apiRequestStatus(this.apiRequestStatus);
        } else {
            this.apiRequestStatus = apiRequestStatus;
        }
    };

    // AIPPT 相关方法
    @action handleAIPPTHistoryMessage = async (historyMessage, session = {}, changeSessionMapItem = null) => {
        try {
            console.log('🔥 AIPPTStore - 处理AIPPT历史消息:', historyMessage);

            // 🔥 对AIPPT历史消息进行安全检查和格式化
            const safeHistoryMessages = historyMessage.map((msg, index) => {
                if (!msg || !msg.content) {
                    return msg;
                }

                let safeContent = msg.content;

                // 检查 content 是否为普通对象（非 React 元素）
                if (safeContent && typeof safeContent === 'object' && !React.isValidElement(safeContent) && safeContent.constructor === Object) {
                    // 检查是否为AIPPT相关的数据结构
                    const isAIPPTData =
                        safeContent.ongoing_status !== undefined ||
                        safeContent.theme !== undefined ||
                        safeContent.catalogs !== undefined ||
                        safeContent.title !== undefined ||
                        safeContent.cover_image_list !== undefined ||
                        safeContent.images_url !== undefined ||
                        safeContent.color !== undefined ||
                        safeContent.style !== undefined ||
                        safeContent.id !== undefined ||
                        safeContent.status !== undefined;

                    if (isAIPPTData) {
                        // 保持原始数据结构，让 AIPPT/index.jsx 的 getItem() 函数处理
                        console.log('🔥 AIPPTStore - 检测到AIPPT数据结构，保持原始content:', {
                            content_type: msg.content_type,
                            handler: msg.handler,
                            role: msg.role,
                            hasOngoingStatus: safeContent.ongoing_status !== undefined,
                            hasCatalogs: safeContent.catalogs !== undefined,
                            hasCoverImages: safeContent.cover_image_list !== undefined,
                            hasTheme: safeContent.theme !== undefined,
                            hasImagesUrl: safeContent.images_url !== undefined
                        });
                    } else {
                        // 其他类型的对象，转换为JSON字符串以便显示
                        safeContent = JSON.stringify(safeContent, null, 2);
                    }
                }

                // 🔥 关键修复：完整保留所有 AIPPT 相关字段
                // 这些字段对于 AIPPT/index.jsx 的 getItem() 正确渲染至关重要

                // 🔥 新增：自动检测是否为AIPPT内容（即使handler不是'aippt'）
                const hasAipptContentType = msg.content_type && msg.content_type.startsWith('aippt_');
                const shouldSetAipptHandler = hasAipptContentType || msg.handler === 'aippt';

                return {
                    ...msg,
                    content: safeContent,
                    // 🔥 核心字段：确保这些字段不会丢失
                    content_type: msg.content_type || (msg.handler === 'aippt' ? 'aippt_catalog' : undefined),
                    // 🔥 关键修复：如果消息包含AIPPT相关的content_type，自动设置handler为'aippt'
                    // 这样即使在通用技能下生成的AIPPT内容，也能正确渲染
                    handler: shouldSetAipptHandler ? 'aippt' : msg.handler,
                    message_type: msg.message_type,
                    // 🔥 额外保留的AIPPT专用字段
                    _id: msg._id,
                    agent_id: msg.agent_id,
                    session_id: msg.session_id,
                    skill_id: msg.skill_id,
                    role: msg.role,
                    create_time: msg.create_time,
                    update_time: msg.update_time,
                    // 🔥 保留可能的API数据
                    apiData: msg.apiData,
                    pptData: msg.pptData,
                    pptId: msg.pptId
                };
            });

            // 更新 sessionMap 中的AIPPT会话数据
            const sessionUpdater = changeSessionMapItem || this.changeSessionMapItem;
            if (sessionUpdater && typeof sessionUpdater === 'function') {
                // 🔥 修复：使用正确的字段名，session.session_id而不是session.sessionId
                const sessionId = session?.session_id || session?.sessionId;

                // 🔥 新增：更新localStorage中的AIPPT会话ID，确保AIPPT组件能正确读取
                // 🔥 修复：不仅检查handler，还检查content_type（支持通用技能下的AIPPT内容）
                const hasAipptContent = historyMessage && historyMessage.length > 0 && (
                    historyMessage[0]?.handler === 'aippt' ||
                    historyMessage.some(msg => msg.content_type && msg.content_type.startsWith('aippt_'))
                );

                if (sessionId && hasAipptContent) {
                    localStorage.setItem('aippt_prev_session_id', sessionId);
                }

                sessionUpdater(
                    sessionId,
                    {
                        messages: safeHistoryMessages,
                        loading: false,
                        answering: false
                    },
                    safeHistoryMessages
                );
            }
        } catch (error) {
            console.error('🔥 AIPPTStore - 处理AIPPT历史记录出错:', error);
        }
    };

    /**
     * 检查是否为AIPPT消息
     * @param {Array} messages - 消息数组
     * @returns {boolean} - 是否为AIPPT消息
     */
    isAipptMessage = (messages) => {
        // 🔥 修复：不仅检查handler，还检查content_type（支持通用技能下的AIPPT内容）
        return messages && messages.length > 0 && (
            messages[0]?.handler === 'aippt' ||
            messages.some(msg => msg.content_type && msg.content_type.startsWith('aippt_'))
        );
    };

    /**
     * 设置AIPPT会话ID到localStorage
     * @param {string} sessionId - 会话ID
     */
    setAipptSessionId = (sessionId) => {
        if (sessionId) {
            localStorage.setItem('aippt_prev_session_id', sessionId);
        }
    };

    /**
     * 获取AIPPT会话ID从localStorage
     * @returns {string|null} - 会话ID
     */
    getAipptSessionId = () => {
        return localStorage.getItem('aippt_prev_session_id');
    };


}

const AIPPTStoreInstance = new AIPPTStore();
export default AIPPTStoreInstance;
